import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ArrowRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { WHATSAPP_URL } from "@/lib/whatsapp";
import gerbenImg from "@/assets/review-gerben.webp";
import bramImg from "@/assets/review-bram.webp";
import savannahImg from "@/assets/team-savannah.webp";

type Block = { nl: string; en: string };
type Case = {
  id: string;
  company: string;
  sector: { nl: string; en: string };
  stat: string;
  statLabel: { nl: string; en: string };
  quote: { nl: string; en: string };
  person: string;
  role: { nl: string; en: string };
  photo: string;
  situation: Block;
  approach: Block;
  result: Block;
};

const cases: Case[] = [
  {
    id: "vitamunda",
    company: "Vitamunda BV",
    sector: { nl: "Health & operations", en: "Health & operations" },
    stat: "100%",
    statLabel: {
      nl: "projecten zelfstandig binnen tijd en budget",
      en: "projects delivered on time and on budget",
    },
    quote: {
      nl: "Ze doet wat ze zegt, denkt mee, lost problemen op en voorkomt ze. Analytisch sterk en voert projecten zelfstandig kundig en binnen tijd uit.",
      en: "She does what she says, thinks along, solves problems and prevents them. Analytically strong and runs projects independently, on time.",
    },
    person: "Gerben de Jong",
    role: { nl: "Owner — Vitamunda BV", en: "Owner — Vitamunda BV" },
    photo: gerbenImg,
    situation: {
      nl: "Marketing- en operationele projecten zoals onze nieuwe website liepen door elkaar, deadlines schoven en problemen werden pas zichtbaar als ze al escaleerden.",
      en: "Marketing and operational projects ran into each other, deadlines slipped and issues only surfaced once they had escalated.",
    },
    approach: {
      nl: "Strakke projectstructuur, wekelijkse voortgang en proactief problemen signaleren vóór ze impact hadden — eigenaarschap van A tot Z.",
      en: "Tight project structure, weekly progress and proactively flagging issues before they hit — full ownership end-to-end.",
    },
    result: {
      nl: "Projecten consistent binnen tijd en budget opgeleverd, met heldere communicatie richting directie.",
      en: "Projects consistently delivered on time and on budget, with clear communication to leadership.",
    },
  },
  {
    id: "goohoo",
    company: "GooHoo.nl",
    sector: { nl: "E-commerce", en: "E-commerce" },
    stat: "3 weken",
    statLabel: {
      nl: "tot meetbare lift in Google Ads-performance",
      en: "to measurable lift in Google Ads performance",
    },
    quote: {
      nl: "Ellen heeft in een paar weken onze Adwords-marketing naar een hoger niveau getild. Onderbouwd advies, doorgevoerd, gemonitord. Wekelijks nieuwe inzichten die ons verder brachten.",
      en: "In just a few weeks Ellen took our AdWords marketing to a higher level. Substantiated advice, implemented and monitored. Weekly new insights that moved us forward.",
    },
    person: "Bram Hoogers",
    role: { nl: "Oprichter — GooHoo.nl", en: "Founder — GooHoo.nl" },
    photo: bramImg,
    situation: {
      nl: "Google Ads draaide, maar zonder duidelijke structuur: te brede zoektermen, geen heldere ROAS en ongebruikte campagnetypes.",
      en: "Google Ads was running, but without structure: broad keywords, no clear ROAS and unused campaign types.",
    },
    approach: {
      nl: "Account-audit, onderbouwd advies op campagnestructuur, biedstrategie en zoekwoorden, daarna iteratief uitrollen en monitoren.",
      en: "Account audit, substantiated advice on campaign structure, bidding and keywords, then iterative rollout and monitoring.",
    },
    result: {
      nl: "Binnen 3 weken meetbare lift in performance en een account dat structureel zelf bij te sturen is.",
      en: "Measurable lift within 3 weeks and an account that can be steered structurally going forward.",
    },
  },
  {
    id: "loislee",
    company: "loislee.nl",
    sector: { nl: "Retail / brand partners", en: "Retail / brand partners" },
    stat: "+8%",
    statLabel: {
      nl: "omzetgroei in een reorganisatiejaar",
      en: "revenue growth in a reorganisation year",
    },
    quote: {
      nl: "Ellen heeft een grote impact gehad op de structuur en groei van het bedrijf. Ze ziet het geheel, denkt strategisch en pakt marketing met sterke commerciële blik op.",
      en: "Ellen had a major impact on our structure and growth. She sees the whole picture, thinks strategically and tackles marketing with a sharp commercial eye.",
    },
    person: "Savannah de Wolff",
    role: { nl: "Manager Brand Partners — loislee.nl", en: "Manager Brand Partners — loislee.nl" },
    photo: savannahImg,
    situation: {
      nl: "Het bedrijf zat midden in een reorganisatie: nieuwe structuur, druk op het team en marketing die opnieuw moest worden ingericht.",
      en: "The company was in the middle of a reorganisation: new structure, pressure on the team and marketing that had to be rebuilt.",
    },
    approach: {
      nl: "Als CEO de organisatie opnieuw ingericht, balans tussen team, doelen en commercie bewaakt en marketing strategisch en commercieel aangestuurd.",
      en: "As CEO, restructured the organisation, balanced team, goals and commerce, and led marketing both strategically and commercially.",
    },
    result: {
      nl: "+8% omzetgroei in een reorganisatiejaar én een stabieler team en duidelijkere richting voor de jaren erna.",
      en: "+8% revenue growth in a reorganisation year, plus a more stable team and clearer direction going forward.",
    },
  },
];

const labels = {
  nl: {
    eyebrow: "04 — Cases & resultaten",
    h2: "Resultaten.",
    sub: "Drie cases van mensen die met Ellen werkten.",
    cta: "Stuur ons jouw case via WhatsApp",
    situation: "Situatie",
    approach: "Aanpak",
    result: "Resultaat",
  },
  en: {
    eyebrow: "04 — Cases & results",
    h2: "Marketing that understands the tech — and proves it.",
    sub: "No marketing speak. Three cases from people who worked with Ellen — metric, quote, context.",
    cta: "Send us your case via WhatsApp",
    situation: "Situation",
    approach: "Approach",
    result: "Result",
  },
} as const;

const CasesResultsSection = () => {
  const { lang } = useI18n();
  const L = labels[lang];
  const [active, setActive] = useState(0);
  const c = cases[active];

  return (
    <section
      id="cases"
      className="py-16 md:py-24 bg-brand-black border-t border-white/5"
    >
      <div className="container mx-auto px-6 max-w-[960px]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-10"
        >
          <div className="section-label mb-3">{L.eyebrow}</div>
          <h2 className="font-display text-3xl md:text-5xl text-white leading-[1.1] mb-4 max-w-[760px]">
            {L.h2}
          </h2>
          <p className="font-light text-[15px] leading-[1.7] text-white/70 max-w-[680px]">
            {L.sub}
          </p>
        </motion.div>

        {/* Stat strip / case selector */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          {cases.map((cc, i) => {
            const isActive = i === active;
            return (
              <button
                key={cc.id}
                onClick={() => setActive(i)}
                className={`text-left p-5 rounded-sm border transition-all ${
                  isActive
                    ? "bg-brand-blue/10 border-brand-blue/50"
                    : "bg-white/[0.02] border-white/10 hover:border-white/25"
                }`}
              >
                <div
                  className={`font-display text-3xl md:text-4xl leading-none mb-1.5 ${
                    isActive ? "text-brand-blue" : "text-white"
                  }`}
                >
                  {cc.stat}
                </div>
                <div className="text-[12px] leading-snug text-white/65 mb-3">
                  {cc.statLabel[lang]}
                </div>
                <div className="flex items-center gap-2 pt-3 border-t border-white/10">
                  <img
                    src={cc.photo}
                    alt={cc.person}
                    className="w-7 h-7 rounded-full object-cover border border-white/15"
                  />
                  <div>
                    <div className="text-[11px] font-semibold text-white tracking-wide leading-tight">
                      {cc.company}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/45">
                      {cc.sector[lang]}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Active case detail */}
        <AnimatePresence mode="wait">
          <motion.div
            key={c.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="relative bg-white/[0.03] border border-white/10 rounded-sm p-6 md:p-9 overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-[3px] h-full bg-brand-blue" />
            <Quote
              className="absolute top-5 right-5 text-brand-blue/25"
              size={40}
              strokeWidth={1}
            />

            <blockquote className="font-display text-lg md:text-xl text-white leading-[1.5] mb-7 max-w-[720px]">
              "{c.quote[lang]}"
            </blockquote>

            <div className="grid md:grid-cols-3 gap-4 md:gap-5 mb-7 pt-6 border-t border-white/10">
              {[
                { label: L.situation, text: c.situation[lang], highlight: false },
                { label: L.approach, text: c.approach[lang], highlight: false },
                { label: L.result, text: c.result[lang], highlight: true },
              ].map((b) => (
                <div key={b.label}>
                  <div
                    className={`text-[10px] font-semibold uppercase tracking-[0.22em] mb-2 ${
                      b.highlight ? "text-brand-blue" : "text-white/45"
                    }`}
                  >
                    {b.label}
                  </div>
                  <p className="font-light text-[13.5px] leading-[1.6] text-white/80">
                    {b.text}
                  </p>
                </div>
              ))}
            </div>

            <footer className="flex items-center gap-4 pt-5 border-t border-white/10">
              <img
                src={c.photo}
                alt={c.person}
                className="w-12 h-12 rounded-full object-cover border border-white/15"
              />
              <div>
                <div className="text-white font-semibold text-sm">{c.person}</div>
                <div className="text-white/60 text-xs mt-0.5">{c.role[lang]}</div>
              </div>
            </footer>
          </motion.div>
        </AnimatePresence>

        <div className="mt-8 flex justify-center">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-brand-blue text-brand-black font-semibold text-[12px] uppercase tracking-[0.18em] px-6 py-3 rounded-sm hover:bg-brand-blue/90 transition-colors"
          >
            {L.cta}
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default CasesResultsSection;
